import React, { useEffect, useRef, useState } from 'react';
import { Table, Button, Spinner, Form } from 'react-bootstrap';
import { fetchProducts, fetchOpenBox, fetchByket, patchProduct, fetchLightSwitchBox } from '../api';
import bellMp3 from './bell.mp3';
import useSound from 'use-sound';

const WS_URL = process.env.REACT_APP_WS_BASE || '';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [pending, setPending] = useState(null);
  const [bouquets, setBykets] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const wsRef = useRef(null);

  const [playBell] = useSound(bellMp3, { volume: 0.5, interrupt: true });
  const prevLocksRef = useRef({});
  useEffect(() => {
    const load = async () => {
      try {
        const data_bykets = await fetchByket();
        setBykets(data_bykets);
        const data = await fetchProducts();
        // Изначально добавляем статус "Unknown" для ясности
        setProducts(data.map(p => ({ ...p, ledStatus: 'Off', lockStatus: 'Locked' })));
      } catch (e) {
        console.error('Не вдалося завантажити продукти:', e);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const ws = new WebSocket(`${WS_URL}/ws`);
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);

      if (msg.snapshot) {
        setProducts((prev) =>
          prev.map((p) => {
            const cell = msg.snapshot[p.name];
            return cell
              ? {
                  ...p,
                  ledStatus: cell.light === 1 ? 'On' : 'Off',
                  lockStatus: cell.lock === 1 ? 'Unlocked' : 'Locked',
                }
              : p;
          }),
        );
        return;
      }

      if (msg.cell) {
        setProducts((prev) =>
          prev.map((p) =>
            p.name === msg.cell
              ? {
                  ...p,
                  ledStatus: msg.light === 1 ? 'On' : 'Off',
                  lockStatus: msg.lock === 1 ? 'Unlocked' : 'Locked',
                }
              : p,
          ),
        );
      }
    };

    ws.onclose = () => console.warn('WS closed');
    return () => ws.close();
  }, []);

  useEffect(() => {
    products.forEach(p => {
      const key = p.id ?? p.name;
      const prev = prevLocksRef.current[key];

      if (prev && prev !== p.lockStatus) {
        playBell();
      }
      prevLocksRef.current[key] = p.lockStatus;
    });
  }, [products, playBell]);

  const openBox = async cellName => {
    try {
      setPending(cellName);
      await fetchOpenBox(cellName);

      setProducts(prev =>
        prev.map(p =>
          p.name === cellName ? { ...p, lockStatus: 'Unlocked' } : p,
        ),
      );
    } catch (err) {
      console.error('Не вдалося відкрити комірку:', err);
      alert(err.message || 'Помилка відкриття комірки');
    } finally {
      setPending(null);
    }
  };

  const handleBouquetChange = async (productId, bouquetId, cellName) => {
    try {
      setPendingId(productId);
      await patchProduct(productId, { byket_id: bouquetId || null });
      await fetchLightSwitchBox(cellName);

      // оптимистично обновляем UI
      setProducts(prev =>
        prev.map(p =>
          p.id === productId
            ? {
                ...p,
                byket_id: bouquetId || null,
                name_byket: bouquets.find(b => b.id === Number(bouquetId))?.name ?? '',
              }
            : p,
        ),
      );
    } catch (e) {
      console.error('Не вдалося оновити букет:', e);
      alert(e.message || 'Помилка оновлення');
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="p-3">
      <h2>Продукти</h2>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Комірка</th>
            <th>Назва букета</th>
            <th>Ціна</th>
            <th>Знаходится</th>
            <th>Категорія</th>
            <th>Зображення</th>
            <th>Статус LED</th>
            <th>Статус замку</th>
            <th>Дія</th>
            <th>Букет</th>
            <th>#</th>
          </tr>
        </thead>

        <tbody>
          {products.map((p, idx) => (
            <tr key={p.id}>
              <td>{p.name}</td>
              <td>{p.name_byket || '—'}</td>
              <td>{p.price} UAH</td>
              <td>{p.time_ago}</td>
              <td>{p.category?.name}</td>
              <td>
                {p.image_data ? (
                  <img
                    src={p.image_data}
                    alt={p.name}
                    style={{ maxWidth: 100, height: 'auto' }}
                  />
                ) : 'Немає зображення'}
              </td>


              {/* Статус LED с текстом */}
              <td>
                <Button variant={p.ledStatus === 'On' ? 'success' : 'secondary'} disabled>
                  {p.ledStatus}
                </Button>
              </td>

              {/* Статус замка с текстом */}
              <td>
                <Button variant={p.lockStatus === 'Unlocked' ? 'success' : 'danger'} disabled>
                  {p.lockStatus === 'Unlocked' ? 'Відкрито' : 'Закрито'}
                </Button>
              </td>

              {/* Кнопка "Відкрити" */}
              <td>
                <Button
                  variant="primary"
                  size="sm"
                  // disabled={p.lockStatus === 'Unlocked' || pending === p.name}
                  onClick={() => openBox(p.name)}
                >
                  {pending === p.name ? <Spinner size="sm" /> : 'Відкрити'}
                </Button>
              </td>
              <td>
                  <Form.Select
                    size="sm"
                    value={p.byket_id ?? ''}
                    disabled={pendingId === p.id}
                    onChange={e => handleBouquetChange(p.id, e.target.value, p.name)}
                  >
                    <option value="">— обрати букет —</option>
                    {bouquets.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Form.Select>
                </td>
              <td>{idx + 1}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default Products;
